import { Button, Form, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Grid as ZhGrid, GridItem as ZhGridItem, Input as ZhInput } from "zhihao-ui-react";

import request from "@/api";
import { confirmDialog } from "@/utils/confirmDialog";
import { useUserStore } from "@/views/stores";
import type { SystemRole } from "./types";
import { RolePermissionSelector } from "./RolePermissionSelector";
import "../../productManagement/ConsignmentFormPage.scss";
import "./RoleDetailPage.scss";

export interface RoleFormValues {
    name: string;
    remark?: string;
    permissions: string[];
}

export interface RoleFormPageProps {
    loadRole?: (id: number | string) => Promise<SystemRole>;
    saveRole?: (id: number | string | undefined, values: RoleFormValues) => Promise<void>;
    permissions?: string[];
}

/** 复用旧 Vue `/admin/role/:id` 角色详情接口。 */
export async function loadRole(id: number | string): Promise<SystemRole> {
    const response = await request.service.get<SystemRole>(`/admin/role/${id}`);
    return response.data;
}

/** 复用旧 Vue 角色新增/编辑接口。 */
export async function saveRole(id: number | string | undefined, values: RoleFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/role", values);
        return;
    }
    await request.service.put(`/admin/role/${id}`, values);
}

interface RoleFormState {
    name: string;
    remark?: string;
    permissions: string[];
}

/** 角色新增/编辑页，先覆盖角色基本信息和权限编码提交契约。 */
export function RoleFormPage({ loadRole: load = loadRole, saveRole: save = saveRole, permissions }: RoleFormPageProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const [form] = Form.useForm<RoleFormState>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const id = new URLSearchParams(location.search).get("id") || undefined;
    // 对齐 Vue v-auth：角色写权限由路由会话提供，也允许测试/宿主显式覆盖权限集合。
    const currentPermissions = permissions ?? storePermissions;
    const canWrite = currentPermissions.includes("ROLE_ROLE_W");

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void load(id)
            .then(info => {
                if (!disposed) {
                    form.setFieldsValue({
                        name: info.name || "",
                        remark: info.remark,
                        permissions: info.permissions || []
                    });
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("角色信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [form, id, load]);

    const handleFinish = async (values: RoleFormState) => {
        // Vue 角色页禁止空权限保存，避免创建出无法使用的角色。
        if (!values.permissions?.length) {
            setErrorMessage("请选择权限");
            return;
        }
        try {
            // 旧 Vue BaseService.create/update 统一使用“是否保存”确认，确认前不写入角色。
            if (!(await confirmDialog("是否保存"))) return;
            await save(id, { name: values.name, remark: values.remark, permissions: values.permissions || [] });
            void navigate(-1);
        } catch {
            setErrorMessage("角色保存失败");
        }
    };

    return (
        <section
            className="consignment-form-page role-form-page flex h-full min-h-full flex-col"
            aria-label="role-form-page"
        >
            <div className="section-card flex-1">
                <div className="operation-fixed-btn">
                    <Button onClick={() => void navigate(-1)}>返回</Button>
                    {canWrite ? (
                        <Button type="primary" onClick={() => form.submit()}>
                            保存
                        </Button>
                    ) : null}
                </div>
                <Typography.Title className="mb-6 mt-0" level={4}>
                    {id ? "编辑角色" : "新增角色"}
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form<RoleFormState> form={form} layout="vertical" onFinish={values => void handleFinish(values)}>
                    <div className="section-title mb-5">角色信息</div>
                    <ZhGrid cols={3} gap={[0, 16]}>
                        <ZhGridItem>
                            <Form.Item
                                className="role-form-basic-item"
                                label="角色名称"
                                name="name"
                                rules={[{ required: true, message: "请输入角色名称" }]}
                            >
                                <ZhInput clearable maxLength={20} placeholder="请输入角色名称" showWordLimit />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item className="role-form-basic-item" label="角色说明" name="remark">
                                <ZhInput clearable maxLength={128} showWordLimit />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={3}>
                            <div className="section-title mb-5 mt-4">配置权限</div>
                            <Form.Item name="permissions">
                                <RolePermissionSelector showSelectAll={false} />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                </Form>
            </div>
        </section>
    );
}

export default RoleFormPage;
